import {
  Activity,
  ChevronRight,
  Globe,
  MapPin,
  Plus,
  Search,
  Share2,
  Swords,
  Tag,
} from "lucide-react";
import { useGetList, useLocaleState, useTranslate } from "ra-core";
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { CompetitorAvatar } from "./CompetitorAvatar";
import { CompetitorForm } from "./CompetitorForm";
import {
  filterCompetitors,
  latestUpdateByCompetitor,
  LINE_FILTERS,
  STATUS_FILTERS,
  type LineFilter,
  type StatusFilter,
} from "./competitors";
import {
  EmptyState,
  ExternalAnchor,
  LoadingCards,
  PillGroup,
  SectionLabel,
} from "@/components/market-intel/components";
import { formatShortDate } from "@/components/market-intel/dates";
import {
  isCompetitorLine,
  isTrackingStatus,
  lineLabelKey,
  trackingStatusLabelKey,
} from "@/components/market-intel/labels";
import { MiniMarkdown } from "@/components/market-intel/MiniMarkdown";
import { latestActivityByCompetitor, searchCompetitors } from "./intel";
import { competitorPath } from "./routes";
import { countByCompetitor } from "./social";
import { TrackingStatusMenu } from "./TrackingStatus";
import type {
  CompetitorLine,
  MarketCompetitor,
  MarketCompetitorUpdate,
} from "@/components/market-intel/types";
import type {
  CompetitorMarketingActivity,
  CompetitorSocialProfile,
} from "./types";
import { hostOf, riseStyle } from "@/components/market-intel/util";

interface Filters {
  line: LineFilter;
  status: StatusFilter;
  query: string;
}

/** URL state: ?linea=…&estado=…&q=… (shareable, survives a reload). */
const useCompetitorFilters = () => {
  const [params, setParams] = useSearchParams();
  const lineParam = params.get("linea");
  const statusParam = params.get("estado");
  const filters: Filters = {
    line: isCompetitorLine(lineParam) ? lineParam : "all",
    status: isTrackingStatus(statusParam) ? statusParam : "all",
    query: params.get("q") ?? "",
  };
  const setFilters = (patch: Partial<Filters>) => {
    const next = { ...filters, ...patch };
    const search: Record<string, string> = {};
    if (next.line !== "all") search.linea = next.line;
    if (next.status !== "all") search.estado = next.status;
    if (next.query) search.q = next.query;
    setParams(search, { replace: true });
  };
  return { ...filters, setFilters };
};

export const CompetitorsView = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const { line, status, query, setFilters } = useCompetitorFilters();

  const {
    data: competitors,
    isPending,
    error,
  } = useGetList<MarketCompetitor>("market_competitors", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "name", order: "ASC" },
  });
  const { data: updates } = useGetList<MarketCompetitorUpdate>(
    "market_competitor_updates",
    {
      pagination: { page: 1, perPage: 2000 },
      sort: { field: "update_date", order: "DESC" },
    },
  );
  const latest = useMemo(
    () => latestUpdateByCompetitor(updates ?? []),
    [updates],
  );
  const { data: profiles } = useGetList<CompetitorSocialProfile>(
    "competitor_social_profiles",
    {
      pagination: { page: 1, perPage: 5000 },
      sort: { field: "id", order: "ASC" },
    },
  );
  const { data: activity } = useGetList<CompetitorMarketingActivity>(
    "competitor_marketing_activity",
    {
      pagination: { page: 1, perPage: 5000 },
      sort: { field: "published_at", order: "DESC" },
    },
  );
  const profileCounts = useMemo(
    () => countByCompetitor(profiles ?? []),
    [profiles],
  );
  const lastActivity = useMemo(
    () => latestActivityByCompetitor(activity ?? []),
    [activity],
  );
  const visible = searchCompetitors(
    filterCompetitors(competitors ?? [], line, status),
    query,
  );
  const filtered = line !== "all" || status !== "all" || query.trim() !== "";

  const addLabel = translate("market.competitors.add");
  const addButton = (
    <Button
      type="button"
      onClick={() => setCreating(true)}
      aria-label={isMobile ? addLabel : undefined}
      title={isMobile ? addLabel : undefined}
      className={cn(
        "btn-save shrink-0 rounded-full",
        isMobile ? "size-9 p-0" : "px-4",
      )}
    >
      <Plus aria-hidden className={isMobile ? "size-5" : "size-4"} />
      {isMobile ? null : addLabel}
    </Button>
  );

  return (
    <section className="space-y-3 md:space-y-4">
      <h2 className="sr-only">{translate("market.competitors.heading")}</h2>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <label className="relative block min-w-0 flex-1 md:max-w-sm">
            <span className="sr-only">
              {translate("competition.list.search")}
            </span>
            <Search
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-3.5 z-10 size-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="search"
              value={query}
              onChange={(event) => setFilters({ query: event.target.value })}
              placeholder={translate("competition.list.search_placeholder")}
              enterKeyHint="search"
              className="liquid-glass h-10 rounded-full border-0 pl-10 text-base md:h-9 md:text-sm"
            />
          </label>
          {isMobile ? addButton : null}
        </div>
        <div className="mi-pills-row md:items-start md:justify-between">
          <PillGroup<LineFilter>
            label={translate("market.lines.label")}
            value={line}
            options={LINE_FILTERS.map((value) => ({
              value,
              label: translate(
                value === "all" ? "market.lines.all" : lineLabelKey(value),
              ),
            }))}
            onChange={(next) => setFilters({ line: next })}
          />
          {isMobile ? null : addButton}
        </div>
        <PillGroup<StatusFilter>
          label={translate("market.tracking_status.label")}
          value={status}
          options={STATUS_FILTERS.map((value) => ({
            value,
            label: translate(
              value === "all"
                ? "market.tracking_status.all"
                : trackingStatusLabelKey(value),
            ),
          }))}
          onChange={(next) => setFilters({ status: next })}
        />
      </div>
      {isPending ? (
        <LoadingCards />
      ) : error ? (
        <EmptyState icon={Swords} title={translate("market.error")} />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={Swords}
          title={translate(
            !competitors?.length
              ? "market.competitors.empty_all"
              : status !== "all" || query.trim()
                ? "market.competitors.empty_filtered"
                : "market.competitors.empty",
          )}
          hint={
            competitors?.length
              ? undefined
              : translate("market.competitors.empty_hint")
          }
          action={
            filtered && competitors?.length ? (
              <Button
                type="button"
                variant="outline"
                className="rounded-full"
                onClick={() =>
                  setFilters({ line: "all", status: "all", query: "" })
                }
              >
                {translate("market.competitors.clear_filters")}
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 xl:grid-cols-3">
          {visible.map((competitor, index) => (
            <CompetitorCard
              key={competitor.id}
              competitor={competitor}
              latestNews={latest.get(String(competitor.id))}
              profiles={profileCounts.get(String(competitor.id)) ?? 0}
              lastActivity={lastActivity.get(String(competitor.id))}
              index={index}
            />
          ))}
        </div>
      )}
      <CompetitorForm
        open={creating}
        onOpenChange={setCreating}
        onCreated={(created) => navigate(competitorPath(created.id))}
      />
    </section>
  );
};

export const LineBadge = ({ line }: { line: CompetitorLine }) => {
  const translate = useTranslate();
  return (
    <span className="inline-flex h-6 max-w-full items-center rounded-full bg-primary/10 px-2.5 text-xs font-semibold text-primary dark:bg-primary/15">
      <span className="truncate">
        {isCompetitorLine(line) ? translate(lineLabelKey(line)) : line}
      </span>
    </span>
  );
};

const CompetitorCard = ({
  competitor,
  latestNews,
  profiles,
  lastActivity,
  index,
}: {
  competitor: MarketCompetitor;
  latestNews?: string;
  profiles: number;
  lastActivity?: string;
  index: number;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const titleId = `mi-competitor-${competitor.id}`;

  return (
    <article
      aria-labelledby={titleId}
      data-interactive
      className="mi-card mi-rise flex min-w-0 flex-col gap-3 p-4 md:p-5"
      style={riseStyle(index)}
    >
      <div className="flex items-start gap-3">
        <CompetitorAvatar competitor={competitor} />
        <div className="min-w-0 flex-1 space-y-1.5">
          <h3
            id={titleId}
            className="text-[17px] leading-snug font-semibold wrap-anywhere"
          >
            <Link
              to={competitorPath(competitor.id)}
              className="outline-none hover:underline focus-visible:underline"
            >
              {competitor.name}
            </Link>
          </h3>
          <div className="flex flex-wrap items-center gap-1.5">
            <LineBadge line={competitor.line} />
            <TrackingStatusMenu competitor={competitor} />
          </div>
        </div>
      </div>
      {competitor.location || competitor.entry_price || competitor.website ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          {competitor.entry_price ? (
            <span className="inline-flex min-w-0 items-center gap-1 font-semibold">
              <Tag aria-hidden className="size-3.5 shrink-0 text-primary" />
              <span className="sr-only">
                {translate("market.competitors.entry_price")}:{" "}
              </span>
              <span className="truncate">{competitor.entry_price}</span>
            </span>
          ) : null}
          {competitor.location ? (
            <span className="inline-flex min-w-0 items-center gap-1 text-muted-foreground">
              <MapPin aria-hidden className="size-3.5 shrink-0" />
              <span className="sr-only">
                {translate("market.competitors.location")}:{" "}
              </span>
              <span className="truncate">{competitor.location}</span>
            </span>
          ) : null}
          {competitor.website ? (
            <ExternalAnchor href={competitor.website} className="text-sm">
              <Globe aria-hidden className="mr-1 inline size-3.5" />
              {hostOf(competitor.website)}
            </ExternalAnchor>
          ) : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] font-medium text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Share2 aria-hidden className="size-3.5 shrink-0" />
          {profiles
            ? translate("competition.list.profiles", { smart_count: profiles })
            : translate("competition.list.no_profiles")}
        </span>
        <span className="inline-flex items-center gap-1">
          <Activity aria-hidden className="size-3.5 shrink-0" />
          {lastActivity
            ? translate("competition.list.last_activity", {
                date: formatShortDate(lastActivity, locale),
              })
            : translate("competition.list.no_activity")}
        </span>
      </div>
      {competitor.value_proposition ? (
        <div>
          <SectionLabel>
            {translate("market.competitors.value_proposition")}
          </SectionLabel>
          <p className="line-clamp-3 text-[15px] wrap-anywhere">
            {competitor.value_proposition}
          </p>
        </div>
      ) : null}
      {competitor.description ? (
        <div>
          <SectionLabel>
            {translate("market.competitors.what_they_do")}
          </SectionLabel>
          <MiniMarkdown
            source={competitor.description}
            className="line-clamp-4"
          />
        </div>
      ) : null}
      {competitor.pricing && !competitor.entry_price ? (
        <div>
          <SectionLabel>{translate("market.competitors.pricing")}</SectionLabel>
          <p className="text-[15px] font-medium wrap-anywhere">
            {competitor.pricing}
          </p>
        </div>
      ) : null}
      <Link
        to={competitorPath(competitor.id)}
        className="-mx-1 mt-auto flex items-center justify-between gap-2 rounded-xl px-1 pt-2 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <span className="min-w-0 truncate">
          {latestNews
            ? translate("market.competitors.latest_news", {
                date: formatShortDate(latestNews, locale),
              })
            : translate("market.competitors.no_news")}
          {competitor.last_reviewed_at
            ? ` · ${translate("market.competitors.last_reviewed", {
                date: formatShortDate(competitor.last_reviewed_at, locale),
              })}`
            : ""}
        </span>
        <ChevronRight aria-hidden className="size-4 shrink-0" />
      </Link>
    </article>
  );
};
